import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { api } from '../../utils/api';
import { TrendingUp, Users, Clock, CheckCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [statsData, leadsData] = await Promise.all([
          api.getStats(),
          api.getLeads({ limit: 5 })
        ]);
        setStats(statsData);
        setLeads(leadsData.leads);
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading) {
    return <div className="text-muted-foreground">Chargement...</div>;
  }

  const kpiCards = [
    {
      title: 'Total Leads',
      value: stats?.total_leads || 0,
      icon: Users,
      color: 'text-primary'
    },
    {
      title: 'Nouveaux (7j)',
      value: stats?.recent_leads_7d || 0,
      icon: TrendingUp,
      color: 'text-green-600'
    },
    {
      title: 'En attente',
      value: stats?.leads_by_status?.nouveau || 0,
      icon: Clock,
      color: 'text-orange-600'
    },
    {
      title: 'Qualifiés',
      value: stats?.leads_by_status?.qualifié || 0,
      icon: CheckCircle,
      color: 'text-blue-600'
    }
  ];

  const chartData = stats?.leads_by_status ? [
    { status: 'Nouveau', count: stats.leads_by_status.nouveau || 0 },
    { status: 'Contacté', count: stats.leads_by_status.contacté || 0 },
    { status: 'Qualifié', count: stats.leads_by_status.qualifié || 0 },
    { status: 'RDV', count: stats.leads_by_status.rdv_planifié || 0 },
    { status: 'Gagné', count: stats.leads_by_status.gagné || 0 },
    { status: 'Perdu', count: stats.leads_by_status.perdu || 0 }
  ] : [];

  const getStatusBadge = (status) => {
    const variants = {
      nouveau: 'bg-teal-100 text-teal-800',
      contacté: 'bg-blue-100 text-blue-800',
      qualifié: 'bg-green-100 text-green-800',
      perdu: 'bg-red-100 text-red-800'
    };
    return variants[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-2">Dashboard</h1>
        <p className="text-muted-foreground">Vue d'ensemble de vos leads et performances</p>
      </div>

      {/* KPI Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((kpi, idx) => {
          const Icon = kpi.icon;
          return (
            <Card key={idx} className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-muted-foreground">{kpi.title}</p>
                <Icon className={`w-5 h-5 ${kpi.color}`} />
              </div>
              <p className="text-3xl font-bold">{kpi.value}</p>
            </Card>
          );
        })}
      </div>

      {/* Chart */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Répartition des leads par statut</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <XAxis dataKey="status" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Recent Leads */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Leads récents</h3>
        <div className="space-y-3">
          {leads.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucun lead pour le moment</p>
          ) : (
            leads.map((lead) => (
              <div key={lead.id} className="flex items-center justify-between py-3 border-b last:border-0">
                <div>
                  <p className="font-medium">{lead.first_name} {lead.last_name}</p>
                  <p className="text-sm text-muted-foreground">
                    {lead.property_type} • {lead.surface_m2}m² • {lead.postal_code}
                  </p>
                </div>
                <Badge className={getStatusBadge(lead.status)}>
                  {lead.status}
                </Badge>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
};

export default Dashboard;
